/**
 * ParentRedirect — soft 302 bounce to the AIImageSuite.pro parent editor.
 *
 * Iter-31: because the plural `aiimagesuites.pro` (this app) and the
 * singular `aiimagesuite.pro` (the parent editor) look nearly identical,
 * users who land on this app expecting the image editor get an instant,
 * clearly-labeled bounce to the parent instead of hitting a mystery 404.
 *
 * Attached to a small allowlist of paths that unambiguously belong to
 * the editor product:
 *   /editor        — the parent's main workspace
 *   /image-editor  — SEO-friendly alias
 *   /gallery-editor — legacy path some users type
 *   /ai-suite      — brand-alias, mirrors marketing copy
 *   /suite         — short alias
 *
 * NOT included: /edit (too generic — future AIImageSuites route could need it)
 *
 * Redirect uses window.location.replace so the back button doesn't trap
 * the user on our redirect page. Renders a 250ms brutalist "handoff"
 * screen so if the parent is briefly unreachable the user sees where
 * they're being sent (rather than a blank screen).
 */
import { useEffect } from "react";

const PARENT_URL = "https://aiimagesuite.pro";
// Append a lightweight referral hint so the parent's analytics can see
// the fat-finger bounce. Non-blocking — parent ignores unknown params.
const REDIRECT_TARGET = `${PARENT_URL}/?from=aiimagesuites`;

export default function ParentRedirect() {
  useEffect(() => {
    const t = setTimeout(() => {
      // location.replace so back-button skips this redirect page
      window.location.replace(REDIRECT_TARGET);
    }, 250);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      className="min-h-[100dvh] bg-white text-black flex items-center justify-center px-6"
      data-testid="parent-redirect-page"
    >
      <div
        className="max-w-md w-full border-2 border-black p-6"
        style={{ boxShadow: "6px 6px 0 0 #FF2A2A" }}
      >
        <p className="label-mono text-neutral-500 mb-2">WRONG APP · REDIRECTING</p>
        <h1 className="font-display font-black tracking-tighter text-3xl mb-3">
          THIS IS THE CAPTURE APP.
        </h1>
        <p className="font-mono text-sm leading-relaxed mb-5">
          Sending you to <b>aiimagesuite.pro</b> — the image editor.
          <br />
          <span className="text-neutral-500">
            If nothing happens in a second, tap below.
          </span>
        </p>
        <a
          href={REDIRECT_TARGET}
          className="inline-block bg-black text-white border-2 border-black px-6 py-3 font-bold text-sm tracking-wider"
          style={{ boxShadow: "4px 4px 0 0 #FF2A2A" }}
          data-testid="parent-redirect-manual-link"
        >
          OPEN AIIMAGESUITE.PRO →
        </a>
        <p className="label-mono text-neutral-400 text-[10px] mt-4">
          You wanted the editor. This is the mobile capture companion.
        </p>
      </div>
    </div>
  );
}
